import { GenericAbortSignal } from 'axios';

import { HighCostMedicationList } from 'models/HighCostMedicationList';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type AdminHighCostMedicationListsIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  archived?: string;
};

export const adminHighCostMedicationListsQueryKeys = {
  base: ['adminHighCostMedicationLists'],
  index: (params: Record<string, any> = {}) => ['adminHighCostMedicationLists', 'index', params],
};

export async function indexAdminHighCostMedicationLists(
  params: AdminHighCostMedicationListsIndexParams,
  signal?: GenericAbortSignal
) {
  return http
    .get<Paginated<HighCostMedicationList>>('/admin/high_cost_medication_lists', { params, signal })
    .then((response) => response.data);
}

// Resolves one list by id, for a deep link to its slideout: the tab paginates, so a linked list may
// sit on a page the browser never fetched. Asks for the same includes the tab does, because the
// slideout renders the client's name and the cost tier key.
export async function showAdminHighCostMedicationList(id: string, signal?: GenericAbortSignal) {
  return http
    .get<HighCostMedicationList>(`/admin/high_cost_medication_lists/${id}`, {
      params: { include: 'client,tiers' },
      signal,
    })
    .then((response) => response.data);
}

// `description` and `position` are the writable parts of a tier — the two things about it that are
// authored rather than derived from the medications carrying it. Its label and whether it exists at
// all are consequences of those medications, so this payload cannot add, rename or retire one: the
// API permits `id`, `description` and `position` and drops anything else. Every tier is sent with its
// new position rather than a move. See design 001 and 002.
export type AdminHighCostMedicationListTierUpdate = {
  id: string;
  description: string | null;
  position: number;
};

export async function updateAdminHighCostMedicationList({
  id,
  tiers,
}: {
  id: string;
  tiers: AdminHighCostMedicationListTierUpdate[];
}) {
  return http
    .patch<HighCostMedicationList>(
      `/admin/high_cost_medication_lists/${id}`,
      { tiers },
      // The same includes the show request asks for, so the response carries the client and the
      // refreshed tier key and can replace the row the tab already holds.
      { params: { include: 'client,tiers' } }
    )
    .then((response) => response.data);
}

// Soft-archives the list; nothing is hard-deleted. Archiving frees the client for a fresh upload.
export async function destroyAdminHighCostMedicationList(id: string) {
  return http.delete(`/admin/high_cost_medication_lists/${id}`);
}
